import { trpc } from '@/shared/api';
import { useAsyncAction } from '@/shared/lib';

/** 링크 복사 결과 — 클립보드에 쓰지 못하면 운영자가 직접 복사할 수 있게 링크를 함께 돌려준다. */
export type GuestQuoteLinkCopy =
  | { readonly status: 'COPIED' }
  | { readonly status: 'CLIPBOARD_FAILED'; readonly url: string };

export interface UseGuestQuoteLinkResult {
  /** 견적 링크를 받아 클립보드에 쓴다. 서버가 거절하면 undefined. */
  readonly copy: (quoteRequestId: string) => Promise<GuestQuoteLinkCopy | undefined>;
  readonly isPending: boolean;
  readonly error: string | null;
}

const writeToClipboard = async (url: string): Promise<GuestQuoteLinkCopy> => {
  try {
    // 보안 연결(https·localhost)이 아니면 navigator.clipboard 가 없다 — 그때 나는 TypeError 도 복사 실패로 본다.
    await navigator.clipboard.writeText(url);
    return { status: 'COPIED' };
  } catch {
    return { status: 'CLIPBOARD_FAILED', url };
  }
};

/**
 * 비회원 견적 [링크 복사](quoteManagement.getGuestLink, 비회원 견적 설계 G14). 서버가 `APP_WEB_URL` + 서명 링크를 만들어 준다.
 * 읽기 프로시저지만 누를 때만 받고 useQuery 를 쓰지 않는다 — useUtils().client 로 바로 불러 서명 링크가
 * TanStack 조회·뮤테이션 캐시에 남지 않게 한다(휴대폰 보기와 같은 방식).
 */
export const useGuestQuoteLink = (): UseGuestQuoteLinkResult => {
  const utils = trpc.useUtils();
  const action = useAsyncAction(
    async (quoteRequestId: string): Promise<GuestQuoteLinkCopy> => {
      const { url } = await utils.client.quoteManagement.getGuestLink.query({ quoteRequestId });
      return writeToClipboard(url);
    },
    () => undefined,
  );

  return {
    copy: (quoteRequestId) => action.execute(quoteRequestId),
    isPending: action.isPending,
    error: action.error,
  };
};
